import { ColDef, ColGroupDef, GridApi } from 'ag-grid-community';

/**
 * stg-ngx-ag-grid@22 targets ag-grid 32 and re-points showLoadingOverlay/hideOverlay at the
 * v32-only `loading` grid option, which the pinned ag-grid 31.3.4 silently ignores - leaving
 * grids with no loader. Dropping the instance overrides restores the native prototype methods.
 * Remove once ag-grid is aligned to the 32.3.9 that stg-js-ngx-grid actually depends on.
 */
export function restoreGridOverlays(api: GridApi): void {
  if (!api) {
    return;
  }
  delete (api as any).showLoadingOverlay;
  delete (api as any).hideOverlay;
}

/**
 * The same wrapper rewrites `rowSelection` into the ag-grid 32 object form. ag-grid 31.3.4 only
 * recognises the string form - `isRowSelection()` and `isMultiselect()` compare against 'single'
 * and 'multiple' - so an object silently disables row selection. The wrapper also folds
 * `suppressRowClickSelection` into that object and deletes the original option, so it has to be
 * restored here too or every click on a row body re-selects it. Call this from onGridReady for
 * any grid whose user interaction depends on selection.
 * Remove once ag-grid is aligned to the 32.3.9 that stg-js-ngx-grid actually depends on.
 */
export function restoreRowSelection(api: GridApi, mode: 'single' | 'multiple', suppressRowClickSelection = false): void {
  if (!api) {
    return;
  }
  api.setGridOption('rowSelection', mode);
  api.setGridOption('suppressRowClickSelection', suppressRowClickSelection);
}

/**
 * ag-grid 31's `addStylesToElement` skips entries whose value is null/undefined, while the
 * ag-grid shipped with Angular 14 assigned them and therefore cleared the property. Every grid
 * here uses `{ backgroundColor: null }` (or a null `*_BackgroundColor` field) to mean "no
 * colour", so without this the previous colour stays on the cell after a refresh.
 * Remove once those cellStyle callbacks return '' instead of null.
 */
export function normalizeCellStyles<T extends ColDef | ColGroupDef>(colDefs: T[]): T[] {
  return colDefs?.map(colDef => {
    const group = colDef as ColGroupDef;
    if (group.children) {
      return { ...group, children: normalizeCellStyles(group.children) } as T;
    }
    const cellStyle = (colDef as ColDef).cellStyle;
    if (typeof cellStyle !== 'function') {
      return colDef;
    }
    return {
      ...colDef,
      cellStyle: (params: any) => blankNullStyleValues(cellStyle(params))
    } as T;
  });
}

function blankNullStyleValues(style: any): any {
  if (!style) {
    return style;
  }
  const normalized = { ...style };
  for (const key of Object.keys(normalized)) {
    if (normalized[key] == null) {
      normalized[key] = '';
    }
  }
  return normalized;
}
